import { DashboardStatsData } from "../store/use-dashboard-stats";
import ChartCard, { CardHeadline } from "./dashboard/chart-card";
import CollectionsPanel from "./dashboard/collections-panel";
import PaymentPlansPanel from "./dashboard/payment-plans-panel";
import DebtPanel from "./dashboard/debt-panel";
import MovementChart from "./dashboard/movement-chart";
import ChasesChart from "./dashboard/chases-chart";
import BalanceBuckets from "./dashboard/balance-buckets";
import TrendTile from "./dashboard/trend-tile";
import StatNumber from "./dashboard/stat-number";
import {
  SkeletonChart,
  SkeletonMeters,
  Shimmer,
} from "./dashboard/skeleton";

type Props = {
  stats: DashboardStatsData | null;
  /** Genuine first load — show skeletons. Refreshes hold the previous render instead. */
  loading?: boolean;
};

const COMPARE = ["Sales", "Renewals", "Standard", "Non-Standard"] as const;

const n = (v: unknown) => Number(v ?? 0) || 0;

export default function Charts({ stats, loading }: Props) {
  const movement = stats?.momentPaymentTrack?.data ?? null;
  const buckets = stats?.collection?.data ?? null;
  const collections = stats?.collectionPayment?.data ?? null;
  const chases = stats?.successCount?.data ?? null;
  const plans = stats?.paymentPlans ?? null;
  const outstanding = stats?.outstandingCollection?.data ?? null;

  const movementTotal = (movement ?? []).reduce(
    (sum: number, r: any) =>
      sum +
      n(r?.icbCollection) +
      n(r?.debtSureCollectionViaPortal) +
      n(r?.debtSureCollectionViaAgent) +
      n(r?.debtSureCollectionViaRobot),
    0
  );

  const totalChases = (buckets ?? []).reduce(
    (sum: number, r: any) => sum + n(r?.totalChases),
    0
  );

  return (
    <div className="grid grid-cols-12 gap-4">
      {/* Movement leads: the widest card, the longest time series. */}
      <ChartCard
        title="Movement"
        subtitle="Collections per day, by channel"
        className="col-span-8 smallLaptop:col-span-12"
        bodyClassName="h-[300px]"
        loading={loading}
        skeleton={<SkeletonChart className="h-full pt-4" />}
        action={
          <CardHeadline caption="in period">
            <StatNumber value={movementTotal} currency />
          </CardHeadline>
        }
      >
        <MovementChart data={movement} />
      </ChartCard>

      <ChartCard
        title="Collections"
        subtitle="Where the money came from"
        className="col-span-4 smallLaptop:col-span-12"
        loading={loading}
        skeleton={<SkeletonMeters rows={4} />}
      >
        <CollectionsPanel data={collections} />
      </ChartCard>

      <ChartCard
        title="Outstanding balance"
        subtitle="Split by who holds the debt"
        className="col-span-4 smallLaptop:col-span-6 mobile:col-span-12"
        loading={loading}
        skeleton={<SkeletonMeters rows={2} />}
      >
        <DebtPanel data={outstanding} />
      </ChartCard>

      <ChartCard
        title="Chases by balance"
        subtitle="Outstanding amount band"
        className="col-span-4 smallLaptop:col-span-6 mobile:col-span-12"
        loading={loading}
        skeleton={
          <div className="flex flex-col gap-4">
            <div className="h-[150px] flex items-center justify-center">
              <Shimmer className="w-[144px] h-[144px] rounded-full" />
            </div>
            <SkeletonMeters rows={3} />
          </div>
        }
        action={
          <CardHeadline caption="chases">
            <StatNumber value={totalChases} unit="chases" />
          </CardHeadline>
        }
      >
        <BalanceBuckets data={buckets} />
      </ChartCard>

      <ChartCard
        title="Payment plans"
        subtitle="Open, finished and failed"
        className="col-span-4 smallLaptop:col-span-12"
        loading={loading}
        skeleton={<SkeletonMeters rows={3} />}
      >
        <PaymentPlansPanel data={plans} />
      </ChartCard>

      <ChartCard
        title="Total chases"
        subtitle="Debt raised against collected, last 6 months"
        className="col-span-8 smallLaptop:col-span-12"
        bodyClassName="h-[280px]"
        loading={loading}
        skeleton={<SkeletonChart className="h-full pt-4" />}
      >
        <ChasesChart data={chases} />
      </ChartCard>

      <ChartCard
        title="New chases by type"
        subtitle="Last 6 months"
        className="col-span-4 smallLaptop:col-span-12"
        loading={loading}
        skeleton={
          <div className="grid grid-cols-2 gap-3">
            {COMPARE.map((c) => (
              <Shimmer key={c} className="h-[104px] rounded-xl" />
            ))}
          </div>
        }
      >
        <div className="grid grid-cols-2 gap-3">
          {COMPARE.map((type, i) => (
            <TrendTile
              key={type}
              label={type}
              slot={i}
              data={stats?.salesCompare?.[type] ?? null}
            />
          ))}
        </div>
      </ChartCard>
    </div>
  );
}
